import React, { useEffect, useState } from 'react';
import { Menu, Search, LogIn, UserPlus, User as UserIcon, LogOut, Heart, Zap, Layers, ShieldCheck, X } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Link } from 'react-router-dom';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { resolveMediaUrl } from '../admin/MediaUpload';
import WalletPill from './WalletPill';
import LanguageSwitcher from './LanguageSwitcher';
import { useFilter } from '../../context/FilterContext';

function useClock(enabled) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    if (!enabled) return;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [enabled]);
  return now;
}

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ord = (d) => { const s = ['th', 'st', 'nd', 'rd'], v = d % 100; return d + (s[(v - 20) % 10] || s[v] || s[0]); };

function BrandLogo({ onClick }) {
  const { data } = useSite();
  // Logo size from admin (default 40px). Logo NEVER shrinks on mobile.
  const size = data.branding?.logoSize || 40;
  return (
    <Link
      to="/"
      onClick={onClick}
      className="flex items-center gap-1 select-none shrink-0 notranslate"
      translate="no"
      aria-label="Home"
    >
      {data.branding?.logoImage ? (
        <img
          src={resolveMediaUrl(data.branding.logoImage)}
          alt={data.branding?.logoText || 'Logo'}
          style={{ height: `${size}px` }}
          className="w-auto object-contain block"
        />
      ) : (
        <div
          className="font-display font-black tracking-tight leading-none text-white whitespace-nowrap"
          style={{ fontSize: `${Math.round(size * 0.55)}px` }}
        >
          <span>{data.branding?.logoText || 'REDDY'}</span>
          <span className="text-white/85 ml-0.5">{data.branding?.logoAccent || 'ANNA'}</span>
        </div>
      )}
    </Link>
  );
}

function UserDropdown() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className="rounded-full h-7 sm:h-8 px-2 sm:px-3 flex items-center gap-1 text-[10px] sm:text-xs font-bold whitespace-nowrap bg-zinc-900 text-white border border-zinc-700 hover:bg-zinc-800 transition shrink-0"
        aria-label="Account"
      >
        <UserIcon size={12} className="text-white shrink-0" />
        <span className="max-w-[60px] sm:max-w-[80px] truncate">{user.name?.split(' ')[0] || 'Acct'}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60 bg-zinc-950 border-zinc-800 text-zinc-100" align="end" sideOffset={6}>
        <DropdownMenuLabel className="text-zinc-400 text-xs leading-tight">
          <div className="text-zinc-100 text-sm font-bold">{user.name}</div>
          <div className="text-zinc-500">+{user.mobile}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem className="focus:bg-zinc-900 cursor-pointer">
          <Heart size={14} className="mr-2 text-[color:var(--gold)]" /> My Interests ({user.interests?.length || 0})
        </DropdownMenuItem>
        <DropdownMenuItem className="focus:bg-zinc-900 cursor-pointer">
          <UserIcon size={14} className="mr-2" /> Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem onClick={logout} className="text-red-400 focus:bg-red-950/30 cursor-pointer">
          <LogOut size={14} className="mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header({ onLogin, onRegister, onMobileMenu }) {
  const { data } = useSite();
  const { user } = useAuth();
  const { branding, header } = data;
  const now = useClock(header.showClock !== false);
  const { query, setQuery, setCategory } = useFilter();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const onBrand = () => {
    setCategory('home');
    setQuery('');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Visibility flags — all default true so existing sites don't break
  const showLogin    = header.showLogin    !== false;
  const showSignup   = header.showSignup   !== false;
  const showGetB2B   = header.showGetB2B   !== false;
  const showGetB2C   = header.showGetB2C   !== false;
  const showGetId    = header.showGetId    !== false;

  const dateStr = `${monthNames[now.getMonth()]} ${ord(now.getDate())} ${now.getFullYear()}`;
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).toLowerCase();

  const scrollTo = (selector) => {
    const el = document.querySelector(selector);
    if (!el) return;
    const offset = window.innerWidth < 768 ? 120 : 100;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
  };

  const handleGetId = () => {
    setQuery('');
    setCategory('home');
    setTimeout(() => {
      const el = document.getElementById('get-id') || document.getElementById('inplay') || document.getElementById('exchanges');
      if (el) {
        const offset = window.innerWidth < 768 ? 140 : 110;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      }
    }, 50);
  };
  const handleGetB2C = () => { setQuery(''); setCategory('b2c'); scrollTo('#exchanges, #products'); };
  const handleGetB2B = () => { setQuery(''); setCategory('b2b'); scrollTo('#exchanges, #products'); };

  const adminBg = header?.bgColor && header.bgColor !== '#777544' ? header.bgColor : null;
  const headerBackground = adminBg ||
    'linear-gradient(180deg,rgba(255,255,255,.10) 0%,rgba(0,0,0,.15) 100%),linear-gradient(90deg,#7a7946 0%,#68653b 30%,#555231 60%,#383421 100%)';

  // Curved rectangle box button style — bold text & rounded-md box
  const boxBtn = 'rounded-md whitespace-nowrap font-black tracking-wide transition active:scale-95 shrink-0 flex items-center justify-center';
  const goldPill = `${boxBtn} bg-[#ccc77d] hover:bg-[#d8d387] text-[#000000] h-7 sm:h-8 md:h-8.5 px-2 sm:px-3 md:px-3.5 text-[10px] sm:text-xs shadow-xs`;
  const darkPill = `${boxBtn} bg-[#302d2d] hover:bg-[#252323] text-white border border-white/30 h-7 sm:h-8 md:h-8.5 px-2 sm:px-3 md:px-3.5 text-[10px] sm:text-xs shadow-xs`;

  return (
    <div
      className="w-full border-b border-[#2d2b18] select-none transition-all duration-300"
      style={{ background: headerBackground, boxShadow: '0 3px 12px 0 rgba(0,0,0,0.45),inset 0 1px 0 rgba(255,255,255,0.2)' }}
      data-testid="site-header"
    >
      {/*
        Single-row layout — all elements on ONE line, never wrap.
        Logo: fixed size (never shrinks).
        All other elements: shrink at smaller screens via smaller padding/text.
        Order: [Logo] [Search] [Clock] [B2B B2C GetId] [Login Signup] [Lang]
      */}
      <div className="max-w-[1440px] mx-auto px-1.5 sm:px-3 md:px-5 flex items-center gap-1 sm:gap-1.5 md:gap-2 flex-nowrap"
           style={{ minHeight: `${Math.max(52, (data.branding?.logoSize || 40) + 20)}px` }}>

        {/* 1. Left: Logo — logo NEVER shrinks */}
        <div className="flex items-center shrink-0">
          <BrandLogo onClick={onBrand} />
        </div>

        {/* 2. Middle: Search Bar (expanded & moved right) + Clock (moved left beside search bar) */}
        <div className="flex-1 flex items-center justify-center md:justify-start lg:justify-center gap-2 sm:gap-3 lg:gap-4 px-2 min-w-0">
          {/* Desktop/Laptop Search Bar */}
          <div className="hidden md:flex relative w-full max-w-[280px] lg:max-w-[360px] xl:max-w-[420px] shrink">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none shrink-0" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={header.searchPlaceholder || 'Search text on page...'}
              className="w-full pl-8 pr-6 h-7.5 md:h-8 rounded-full bg-[#302d2d] text-white placeholder:text-zinc-400 text-xs border border-black/20 shadow-inner focus-visible:ring-1 focus-visible:ring-[#ccc77d]"
              data-testid="header-search"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white w-4 h-4 rounded-full flex items-center justify-center bg-zinc-800 text-xs"
              >×</button>
            )}
          </div>

          {/* Clock — fully transparent background */}
          {header.showClock !== false && (
            <div className="hidden lg:flex flex-col items-start text-white leading-tight shrink-0 bg-transparent p-0 border-none">
              <span className="text-[9px] text-white/75 font-medium whitespace-nowrap">{dateStr}</span>
              <span className="text-[11px] font-bold tracking-wide whitespace-nowrap text-[#ccc77d]">{timeStr}</span>
            </div>
          )}

          {/* Mobile Search Icon */}
          <button
            type="button"
            onClick={() => setMobileSearchOpen(o => !o)}
            className="md:hidden w-7 h-7 rounded-full bg-black/30 text-white flex items-center justify-center border border-black/20 shrink-0 active:scale-95"
            aria-label="Search"
            data-testid="header-search-icon-mobile"
          >
            <Search size={13} />
          </button>
        </div>

        {/* 4. B2B, B2C, GET ID — curved rectangle boxes, bold text */}
        <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
          {showGetB2B && (
            <button type="button" onClick={handleGetB2B} data-testid="header-btn-get-b2b" className={goldPill}>
              <ShieldCheck size={11} className="shrink-0 hidden sm:inline mr-1" />
              <span>B2B</span>
            </button>
          )}
          {showGetB2C && (
            <button type="button" onClick={handleGetB2C} data-testid="header-btn-get-b2c" className={goldPill}>
              <Layers size={11} className="shrink-0 hidden sm:inline mr-1" />
              <span>B2C</span>
            </button>
          )}
          {showGetId && (
            <button type="button" onClick={handleGetId} data-testid="header-btn-get-id" className={goldPill}>
              <Zap size={11} className="shrink-0 fill-black hidden sm:inline mr-1" />
              <span>GET ID</span>
            </button>
          )}
        </div>

        {/* 5. Login / Signup or User account — shrink on mobile */}
        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          {user ? (
            <>
              <WalletPill inDark />
              <UserDropdown />
            </>
          ) : (
            <>
              {showLogin && (
                <button
                  onClick={onLogin}
                  className={goldPill}
                  data-testid="header-login-btn"
                >
                  <LogIn size={10} className="shrink-0 mr-0.5" />
                  <span>{header.loginLabel || 'Login'}</span>
                </button>
              )}
              {showSignup && (
                <button
                  onClick={onRegister}
                  className={`${darkPill} hidden sm:flex`}
                  data-testid="header-signup-btn"
                >
                  <UserPlus size={10} className="shrink-0 mr-0.5" />
                  <span>{header.registerLabel || 'Signup'}</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* 6. Language Switcher — always last */}
        <div className="shrink-0">
          <LanguageSwitcher compact />
        </div>
      </div>

      {/* Mobile Search Drawer (below header) */}
      {mobileSearchOpen && (
        <div className="md:hidden px-3 py-2 border-t border-black/30" style={{ background: '#403f2a' }}>
          <div className="relative flex items-center">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={header.searchPlaceholder || 'Search...'}
              className="w-full pl-9 pr-8 h-8 rounded-full bg-[#302d2d] text-white text-xs placeholder:text-zinc-400 border border-black/30 focus-visible:ring-1 focus-visible:ring-[#ccc77d]"
              data-testid="header-search-mobile"
            />
            <button
              type="button"
              onClick={() => { setMobileSearchOpen(false); setQuery(''); }}
              className="absolute right-2.5 text-zinc-400 hover:text-white p-1"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
